import { User } from "../Entities/user.entity";
import { EventDispatcher } from "../Utils/event-dispatcher";
import apiClient from "./api-client.service";
import AgentService from "./agent/agent.service";
import messages from "./agent/messages";
import axios from "axios";

export default class AuthService extends EventDispatcher {
  public static EVENTS = {
    USER_SIGNED_IN: "USER_SIGNED_IN",
    USER_SIGNED_OUT: "USER_SIGNED_OUT",
  };
  private APIBaseUrl: string;
  private static instance: AuthService;
  private currentUser: User | null = null;

  private constructor() {
    super();
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
    this.checkTokenAndRestoreCurrentUser();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  async checkTokenAndRestoreCurrentUser() {
    const accessToken = this.getAccessTokenFromLocalStorage();
    console.debug("AuthService: checking for access token:", accessToken);
    if (accessToken) {
      await this.fetchAndStoreCurrentUser();
    } else {
      this.currentUser = null;
    }
  }

  async signIn(data: { email: string; password: string }) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/auth/login`,
        data
      );
      if (response.data && response.data.access_token) {
        this.setAccessTokenToLocalStorage(response.data.access_token);
      }
      await this.fetchAndStoreCurrentUser();
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      } else {
        throw error;
      }
    }
  }

  async handleGoogleAuth(domain: string) {
    try {
      await this.signUpUsingGoogleOAuth(domain);
    } catch (error) {
      console.error("Error while signing up user:", error);
    }
  }

  async signUpUsingGoogleOAuth(domain: string) {
    try {
      // const response = await apiClient.get(`${this.APIBaseUrl}/auth/google?state=${domain}`)
      // return response
      window.location.href = `${this.APIBaseUrl}/auth/google?state=${domain}`;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      } else {
        throw error;
      }
    }
  }

  async handleLinkedinAuth(domain: string) {
    window.location.href = `${this.APIBaseUrl}/auth/linkedin?state=${domain}`;
  }

  async signupProviderTokenVerification(access_token: string) {
    try {
      if (access_token) {
        this.setAccessTokenToLocalStorage(access_token);
      }
      await this.fetchAndStoreCurrentUser();
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      } else {
        throw error;
      }
    }
  }

  async signUpUser(data: any) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/auth/signup`,
        data
      );
      if (response.data && response.data.token) {
        this.setAccessTokenToLocalStorage(response.data.token);
        await this.fetchAndStoreCurrentUser();
      }
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      } else {
        throw error;
      }
    }
  }

  async signOut() {
    await this.clearAccessTokenFromLocalStorage();
    this.setCurrentUser(null);
    AgentService.getInstance().setCurrentMessage(
      messages.JOB_NOT_APPLIED,
      "",
      "Apply Now"
    );
    window.location.href = "/";
  }

  async fetchAndStoreCurrentUser() {
    try {
      const response = await apiClient.get(`${this.APIBaseUrl}/users/me`);
      this.setCurrentUser(response.data);
    } catch (err) {
      console.error("AuthService: current user not found:", err);
    }
  }

  getCurrentUser() {
    return this.currentUser;
  }

  setCurrentUser(user: any) {
    this.currentUser = user;
    if (user) {
      this.dispatch(AuthService.EVENTS.USER_SIGNED_IN, {
        currentUser: user,
      });
    } else {
      this.dispatch(AuthService.EVENTS.USER_SIGNED_OUT);
    }
  }

  setAccessTokenToLocalStorage(token: string) {
    localStorage.setItem("access-token", token);
  }

  getAccessTokenFromLocalStorage(): string | null {
    return localStorage.getItem("access-token");
  }

  clearAccessTokenFromLocalStorage() {
    localStorage.removeItem("access-token");
  }

  async sendPasswordResetMail(email: string) {
    try {
      const formData = new FormData();
      formData.append("email", email);

      const response = await axios.post(
        `${this.APIBaseUrl}/users/forgot-password`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log("🚀 ~ AuthService ~ sendPasswordResetMail ~ error:", error);
    }
  }

  async resetPassword(token: string, password: string) {
    try {
      const formData = new FormData();
      formData.append("token", token);
      formData.append("password", password);

      const response = await axios.post(
        `${this.APIBaseUrl}/users/reset-password`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.log("🚀 ~ AuthService ~ resetPassword ~ error:", error);
    }
  }
}
